import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authenticatedFetch, readErrorMessage } from '../../utils/authenticatedFetch';
import { plainTextForSpeech, splitTextForSpeech } from '../../utils/textForSpeech';

export type VoicePlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

type VoicePlaybackState = {
  activeId: string | null;
  status: VoicePlaybackStatus;
  error: string | null;
};

type SpeakOptions = {
  id: string;
  text: string;
  alreadyNormalized?: boolean;
};

type VoicePlaybackContextValue = VoicePlaybackState & {
  speak: (options: SpeakOptions) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  toggle: (options: SpeakOptions) => Promise<void>;
};

const VoicePlaybackContext = createContext<VoicePlaybackContextValue | null>(null);

const MAX_CACHE_ENTRIES = 12;

export const VoicePlaybackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<VoicePlaybackState>({
    activeId: null,
    status: 'idle',
    error: null,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const cacheRef = useRef<Map<string, string>>(new Map());
  const generationRef = useRef(0);
  const chunkQueueRef = useRef<string[]>([]);
  const chunkIndexRef = useRef(0);
  const activeIdRef = useRef<string | null>(null);
  const statusRef = useRef<VoicePlaybackStatus>('idle');

  const revokeUrl = useCallback((url: string | null) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const stopInternal = useCallback((clearError = true) => {
    generationRef.current += 1;
    chunkQueueRef.current = [];
    chunkIndexRef.current = 0;
    activeIdRef.current = null;
    statusRef.current = 'idle';

    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }

    if (objectUrlRef.current && !Array.from(cacheRef.current.values()).includes(objectUrlRef.current)) {
      revokeUrl(objectUrlRef.current);
    }
    objectUrlRef.current = null;

    setState((prev) => ({
      activeId: null,
      status: 'idle',
      error: clearError ? null : prev.error,
    }));
  }, [revokeUrl]);

  const ensureAudio = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return audioRef.current;
  }, []);

  const fetchChunkUrl = useCallback(async (chunk: string): Promise<string> => {
    const cached = cacheRef.current.get(chunk);
    if (cached) return cached;

    const response = await authenticatedFetch('/api/speak', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: chunk }),
    });

    if (!response.ok) {
      const message = await readErrorMessage(response, 'Could not generate speech.');
      throw Object.assign(new Error(message), { status: response.status });
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (cacheRef.current.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cacheRef.current.keys().next().value;
      if (oldestKey !== undefined) {
        const oldUrl = cacheRef.current.get(oldestKey);
        cacheRef.current.delete(oldestKey);
        if (oldUrl && oldUrl !== objectUrlRef.current) revokeUrl(oldUrl);
      }
    }
    cacheRef.current.set(chunk, url);
    return url;
  }, [revokeUrl]);

  const playFromIndex = useCallback(async (generation: number) => {
    const chunk = chunkQueueRef.current[chunkIndexRef.current];
    if (!chunk) {
      stopInternal(true);
      return;
    }

    try {
      statusRef.current = 'loading';
      setState({
        activeId: activeIdRef.current,
        status: 'loading',
        error: null,
      });

      const url = await fetchChunkUrl(chunk);
      if (generation !== generationRef.current) return;

      const audio = ensureAudio();
      objectUrlRef.current = url;
      audio.src = url;
      audio.onended = () => {
        if (generation !== generationRef.current) return;
        const nextIndex = chunkIndexRef.current + 1;
        if (nextIndex < chunkQueueRef.current.length) {
          chunkIndexRef.current = nextIndex;
          void playFromIndex(generation);
        } else {
          stopInternal(true);
        }
      };
      audio.onerror = () => {
        if (generation !== generationRef.current) return;
        statusRef.current = 'error';
        setState({
          activeId: activeIdRef.current,
          status: 'error',
          error: 'Could not play audio.',
        });
      };

      await audio.play();
      if (generation !== generationRef.current) return;
      statusRef.current = 'playing';
      setState({
        activeId: activeIdRef.current,
        status: 'playing',
        error: null,
      });
    } catch (error) {
      if (generation !== generationRef.current) return;
      statusRef.current = 'error';
      setState({
        activeId: activeIdRef.current,
        status: 'error',
        error: error instanceof Error ? error.message : 'Could not generate speech.',
      });
    }
  }, [ensureAudio, fetchChunkUrl, stopInternal]);

  const speak = useCallback(async ({ id, text, alreadyNormalized }: SpeakOptions) => {
    const normalized = alreadyNormalized ? text.trim() : plainTextForSpeech(text);
    const chunks = splitTextForSpeech(normalized);
    if (!chunks.length) {
      statusRef.current = 'error';
      setState({ activeId: id, status: 'error', error: 'Nothing to read aloud.' });
      return;
    }

    stopInternal(true);
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    activeIdRef.current = id;
    chunkQueueRef.current = chunks;
    chunkIndexRef.current = 0;
    statusRef.current = 'loading';
    setState({ activeId: id, status: 'loading', error: null });
    await playFromIndex(generation);
  }, [playFromIndex, stopInternal]);

  const pause = useCallback(() => {
    if (!audioRef.current || statusRef.current !== 'playing') return;
    audioRef.current.pause();
    statusRef.current = 'paused';
    setState((prev) => ({ ...prev, status: 'paused' }));
  }, []);

  const resume = useCallback(async () => {
    if (!audioRef.current || statusRef.current !== 'paused') return;
    try {
      await audioRef.current.play();
      statusRef.current = 'playing';
      setState((prev) => ({ ...prev, status: 'playing', error: null }));
    } catch {
      statusRef.current = 'error';
      setState((prev) => ({ ...prev, status: 'error', error: 'Could not resume audio.' }));
    }
  }, []);

  const stop = useCallback(() => {
    stopInternal(true);
  }, [stopInternal]);

  const toggle = useCallback(async (options: SpeakOptions) => {
    if (activeIdRef.current === options.id && statusRef.current === 'playing') {
      pause();
      return;
    }
    if (activeIdRef.current === options.id && statusRef.current === 'paused') {
      await resume();
      return;
    }
    await speak(options);
  }, [pause, resume, speak]);

  useEffect(() => {
    const handlePageHide = () => stopInternal(true);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stopInternal(true);
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      stopInternal(true);
      cacheRef.current.forEach((url) => revokeUrl(url));
      cacheRef.current.clear();
    };
  }, [revokeUrl, stopInternal]);

  const value = useMemo<VoicePlaybackContextValue>(
    () => ({
      ...state,
      speak,
      pause,
      resume,
      stop,
      toggle,
    }),
    [pause, resume, speak, state, stop, toggle]
  );

  return <VoicePlaybackContext.Provider value={value}>{children}</VoicePlaybackContext.Provider>;
};

export function useVoicePlayback(): VoicePlaybackContextValue {
  const ctx = useContext(VoicePlaybackContext);
  if (!ctx) {
    throw new Error('useVoicePlayback must be used within VoicePlaybackProvider');
  }
  return ctx;
}
